import React, { useEffect, useState, useRef, useMemo, useLayoutEffect } from 'react';
import { cn } from '@/utils/cn';
import { useToast } from '@/hooks/use-toast';
import { useSpeechRecognizer } from '@/hooks/use-speech-recognizer'
import { Script } from '@/components/common/script';
import { IconButton } from '@/components/common/icon-button';
import { Image } from '@/components/common/image';
import { TruncatedText } from '@/components/common/truncated-text';
import { Tooltip } from '@/components/ui/modal';
import { VoiceTemplateCapsuleItemNew } from './voice-capsule-item';
import { VideoTypeIcon } from './assets/video-type-icon';
import { PodcastTypeIcon } from './assets/podcast-type-icon';
import { MomentsTypeIcon } from './assets/moments-type-icon';
import { PostTypeIcon } from './assets/post-type-icon';
import { ArticleTypeIcon } from './assets/article-type-icon';
import { SendIcon } from './assets/send-icon';
import { RecordIcon } from './assets/record-icon';
import { StopIcon } from './assets/stop-icon';
import { LinkIcon } from './assets/link-icon';
import { ApplyInputTypeInfo } from '@/services/agent-user-subinfo-get';
import { formatDateToYYYYMMDD } from '@/utils/date-formatter';
import { useTranslation } from 'react-i18next';
import { AgentCreditsIcon } from './assets/agent-credits-icon';
import { Plus, X } from 'lucide-react';
import Lottie from 'lottie-react';
import { DocumentChooseModal } from './document-choose-modal';
import { PaperclipIcon } from './assets/paperclip-icon';
import { MoreActionItem, MoreActionPopover } from '../common/more-action-popover';
import { TemplateChooseModal } from './template-choose-modal';
import { TemplateVoiceModal } from './template-voice-modal';
import { findFirstValidLink } from '@/utils/match-text-url';
import { FallbackImage } from '../common/fallback-image';
import { buildImageOssResize } from '@/utils/build-image-oss-resize ';
import { ChangeIcon } from './assets/change-icon';
import { AiCreationTypeIcon } from './assets/ai-creation-icon';
import { AiCreationButton } from './ai-creation-button';
import { CreationTypeButton, ModelButton, ParameterButton } from './ai-creation-components';
import { AiCreationParams, AiCreationType, INPUT_TYPE } from '@/manager/manager/agent-user-messages-manager';
import { reconcileScriptReplaceMarks } from '@/utils/script-utils';
import type { ScriptReplaceMark } from '@/services/make-generate';
import { getAiCreationCredits, getAudioPreviewCredits, getWorkflowCredits } from './constants/credits-mapping';
import { SwitchHorizontalIcon } from './assets/switch-horizontal-icon';
import { getModelOptionByValue } from './constants/ai-creation-model';
import { LiveScriptButton, LiveScriptLabel } from './live-script-button';
import { OutputSettingButton } from './output-setting-button';
import { EstimatedDurationState, getEstimatedDurationSeconds } from './estimated-video-duration';
import { MaterialRangeSettingButton } from './material-range-setting-button';
import { VIDEO_TEMPLATE_TYPE } from '@/manager/manager/agent-user-template-list-manager';
import { getWordCount, trimToWordCount } from '@/utils/text-word-util';
import { VideoTemplateChooseModal } from './video-template-choose-modal';
import { VideoTemplateAddButton } from './video-template-add-button';
import { ScriptCreation } from '../script-creation';
import { AuditionButton } from './audition-button';
import { ScriptIllegalButton } from './script-illegal-button';
import { OutputLangSelect } from './output-lang-select';
import { runScriptIllegalSubmitGate } from '@/utils/script-illegal-submit-gate';
import searchJson from './constants/search.json'
import { LIVE_SCRIPT_COUNT, VIDEO_DURATION_STEP } from './constants';
import { AudioTTSCreateProps } from '@/services/audio-tts-create';
import { HintTooltip } from '../common/hint-tooltip';

const URL_REGEX = /(https?:\/\/[^\s\u4e00-\u9fa5]+)|(www\.[^\s\u4e00-\u9fa5]+)/i;

export const INPUT_TYPE_MAP = {
  1: 'videoApplyInputType',
  2: 'blogApplyInputType',
  3: 'momentsApplyInputType',
  4: 'graphicApplyInputType',
  5: 'articleApplyInputType',
} as const;


/** 视频口播模式下最大输入文本长度 */
const VIDEO_LIVE_SCRIPT_MAX_COUNT = 2400;
/** 播客口播模式下最大输入文本长度 */
const PODCAST_LIVE_SCRIPT_MAX_COUNT = 4800;

/** 工作流非口播模式下默认最大输入文本长度 */
const WORKFLOW_SCRIPT_DEFAULT_MAX_COUNT = 2400;

enum InputMode {
  voice = 'voice',
  keyboard = 'keyboard',
}

type TemplateItem = {
  id: number;
  coverUrl: string;
  name: string;
  templateResource?: number;
  sampleUrl?: string;
  voiceModelId?: string;
  speedRatio?: number;
  // 视频模板类型
  scene?: VIDEO_TEMPLATE_TYPE
};

type VoiceItem = {
  id: string;
  sampleUrl: string;
  name: string;
  voiceTtsId?: string;
};

export type ContentInputProps = {
  isException?: boolean;
  isExpired?: boolean;
  expiredAt?: string;
  showAiCreation?: boolean;
  showVideo?: boolean;
  showPodcast?: boolean;
  showMoments?: boolean;
  showPost?: boolean;
  showArticle?: boolean;
  applyInputTypeInfo?: ApplyInputTypeInfo;
  videoTemplates: TemplateItem[];
  voiceTemplates: VoiceItem[];
  momentTemplates: TemplateItem[];
  postTemplates: TemplateItem[];
  articleTemplates: TemplateItem[];
  showTemplates: boolean;
  onShowTemplatesChange: (v: boolean) => void;
  onSubmit?: (payload: {
    content: string;
    inputType: number;
    templateId?: number;
    voiceId?: string;
    linkUrl?: string;
    noteId?: number;
    noteName?: string;
    templateResource?: number;
    scriptMarks?: ScriptReplaceMark[];
    /** 与输入区展示的积分一致，传给 /make/generate */
    point?: number;
  }) => Promise<void>;

  mode: InputMode,
  inputText: string,
  inputType: number,
  linkUrl?: string,
  outLang?: string,
  estimatedDuration: EstimatedDurationState,
  noteId?: number,
  noteName?: string,
  liveStreamScript?: boolean,
  openScriptIllegal?: boolean,
  scriptRiskWords?: string[];
  scriptIllegalChecking?: boolean;
  saveVoiceTemplateLoading?: boolean,
  voiceTemplateLoading?: boolean,
  aiCreationType?: AiCreationType,
  aiCreationModel?: string,
  aiCreationParams?: AiCreationParams,
  referenceImages?: string[],
  uploadReferenceImagesLoading?: boolean,
  selectedVideoTemplate: TemplateItem,
  selectedVoiceTemplate: VoiceItem,
  selectedMomentTemplate: TemplateItem,
  selectedPostTemplate: TemplateItem,
  selectedArticleTemplate: TemplateItem,
  endFrameImage?: string[],
  endFrameImageLoading?: boolean,
  scriptMarks?: ScriptReplaceMark[];
  isAudioGenerating: boolean;
  managedAudioUrl?: string;

  onRefreshVideoTemplates?: () => void,
  onUploadEndFrameImage?: (files: File | File[]) => void,
  onEndFrameImageChange?: (v: string[]) => void,
  onSwitchFirstAndEndFrame?: () => void,
  onModeChange: (v: InputMode) => void,
  onInputTextChange: (v: string | ((prev: string) => string)) => void,
  onInputTypeChange: (v: number) => void,
  onLiveStreamScriptChange: (v: boolean) => void,
  onOpenScriptIllegalChange: (v: boolean) => void,
  onOutLangChange: (v: string) => void,
  onEstimatedDurationChange: (v: EstimatedDurationState) => void,
  onLinkUrlChange: (v: string) => void,
  onNoteIdChange: (v: number) => void,
  onNoteNameChange: (v: string) => void,
  onAiCreationTypeChange: (v: AiCreationType) => void,
  onAiCreationModelChange: (v: string) => void,
  onAiCreationParamsChange: (v: AiCreationParams) => void,
  onReferenceImagesChange: (v: string[]) => void,
  onSelectedVideoTemplateChange: (v: TemplateItem | undefined) => void,
  onSelectedVoiceTemplateChange: (v: VoiceItem | undefined) => void,
  onSelectedMomentTemplateChange: (v: TemplateItem | undefined) => void,
  onSelectedPostTemplateChange: (v: TemplateItem | undefined) => void,
  onSelectedArticleTemplateChange: (v: TemplateItem | undefined) => void,
  onUploadReferenceImages?: (files: File | File[]) => void,
  onSaveVoiceTemplate?: (voiceIds: string[]) => Promise<void>,
  onRemoveVoiceTemplate?: (voiceId: string) => Promise<void>,
  onCheckScriptIllegal?: (text: string) => Promise<string[]>;
  onIsScriptIllegalCheckFreshForText?: (text: string) => boolean;
  onClearScriptRiskWords?: () => void;
  onScriptMarksChange?: (marks: ScriptReplaceMark[]) => void;
  onGenerateAudio: (params: AudioTTSCreateProps) => Promise<string>;
  onClearTTSCreateResult?: () => void;
};

export const ContentInput: React.FC<ContentInputProps> = ({
  isException,
  isExpired,
  expiredAt,
  showAiCreation = false,
  showVideo = false,
  showPodcast = false,
  showMoments = false,
  showPost = false,
  showArticle = false,
  applyInputTypeInfo,
  videoTemplates,
  voiceTemplates,
  momentTemplates,
  postTemplates,
  articleTemplates,
  showTemplates,
  onShowTemplatesChange,
  onSubmit,

  mode,
  inputText,
  inputType,
  linkUrl,
  outLang,
  estimatedDuration,
  noteId,
  noteName,
  liveStreamScript,
  openScriptIllegal,
  scriptRiskWords = [],
  scriptIllegalChecking = false,
  onCheckScriptIllegal,
  onIsScriptIllegalCheckFreshForText,
  onClearScriptRiskWords,
  onClearTTSCreateResult,
  saveVoiceTemplateLoading,
  voiceTemplateLoading,
  aiCreationType,
  aiCreationModel,
  aiCreationParams,
  referenceImages,
  uploadReferenceImagesLoading,
  selectedVideoTemplate,
  selectedVoiceTemplate,
  selectedMomentTemplate,
  selectedPostTemplate,
  selectedArticleTemplate,
  endFrameImage,
  endFrameImageLoading,
  onUploadEndFrameImage,
  onEndFrameImageChange,
  onUploadReferenceImages,
  onSwitchFirstAndEndFrame,
  onRefreshVideoTemplates,
  onSaveVoiceTemplate,
  onRemoveVoiceTemplate,
  scriptMarks = [],
  onScriptMarksChange,
  isAudioGenerating,
  managedAudioUrl,
  onGenerateAudio,
  onModeChange: setMode,
  onInputTextChange: setValue,
  onInputTypeChange: setInputType,
  onLinkUrlChange: setLinkUrl,
  onNoteIdChange: setNoteId,
  onNoteNameChange: setNoteName,
  onLiveStreamScriptChange: setLiveStreamScript,
  onOpenScriptIllegalChange: setOpenScriptIllegal,
  onAiCreationTypeChange: setAiCreationType,
  onAiCreationModelChange: setAiCreationModel,
  onAiCreationParamsChange: setAiCreationParams,
  onReferenceImagesChange: setReferenceImages,
  onSelectedVideoTemplateChange: setSelectedVideoTemplate,
  onSelectedVoiceTemplateChange: setSelectedVoiceTemplate,
  onSelectedMomentTemplateChange: setSelectedMomentTemplate,
  onSelectedPostTemplateChange: setSelectedPostTemplate,
  onSelectedArticleTemplateChange: setSelectedArticleTemplate,
  onOutLangChange: setOutLang,
  onEstimatedDurationChange: setEstimatedDuration,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  // 是否锁定操作（By音频生成中）
  const shouldBlockByAudioGenerating = isAudioGenerating;
  // 是否锁定操作（By违规词检测中）
  const shouldBlockByScriptIllegalChecking = scriptIllegalChecking;

  // const [mode, setMode] = useState<InputMode>(InputMode.voice);
  // const [inputType, setInputType] = useState<number>(1);
  // const [selectedVideoTemplate, setSelectedVideoTemplate] = useState<TemplateItem | undefined>();
  // const [selectedVoiceTemplate, setSelectedVoiceTemplate] = useState<VoiceItem | undefined>();
  // const [selectedMomentTemplate, setSelectedMomentTemplate] = useState<TemplateItem | undefined>();
  // const [selectedPostTemplate, setSelectedPostTemplate] = useState<TemplateItem | undefined>();
  // const [selectedArticleTemplate, setSelectedArticleTemplate] = useState<TemplateItem | undefined>();
  // const [value, setValue] = useState('');
  const scriptRef = useRef<HTMLTextAreaElement | HTMLDivElement | null>(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showTemplateChooseModal, setShowTemplateChooseModal] = useState(false);
  const [showVideoTemplateChooseModal, setShowVideoTemplateChooseModal] = useState(false);
  const [showVoiceTemplateModal, setShowVoiceTemplateModal] = useState(false);
  const [showRiskTipsModal, setShowRiskTipsModal] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  // 口播台词标签实际渲染宽度（多语言文案宽度不同，用于动态计算输入框首行缩进，避免间隔过宽）
  const [scriptLabelWidth, setScriptLabelWidth] = useState(68);

  const { recording, stopIn, toggle } = useSpeechRecognizer({
    lang: 'zh-CN',
    onFinalResult: (text) => setValue((prev) => (prev ? prev + text : text)),
    onStart: () => { setMode(InputMode.voice); },
    onError: (err) => { console.error(err); toast.default({ description: t('agent_creation.recording.asr_fail') }); },
    onAsrStart: () => { toast.default({ description: t('agent_creation.recording.asr_start') }); },
    onAsrEnd: (text) => {
      if (text) { toast.default({ description: t('agent_creation.recording.asr_end') }); }
      else { toast.default({ description: t('agent_creation.recording.asr_no_text') }); }
    },
  });

  // 输入文本字数统计
  const inputTextCount = getWordCount(inputText);

  useEffect(() => {
    // 定义展示优先级顺序及其对应的 ID
    const priorityConfigs = [
      { visible: showVideo, id: 1 },
      { visible: showAiCreation, id: 6 },
      { visible: showPost, id: 4 },
      { visible: showArticle, id: 5 },
      { visible: showPodcast, id: 2 },
      { visible: showMoments, id: 3 },
    ];

    // 找到第一个为 true 的配置项
    const activeItem = priorityConfigs.find(config => config.visible);

    if (activeItem) {
      setInputType(activeItem.id);
    }

    return () => setInputType(null);
  }, [showAiCreation, showVideo, showPodcast, showMoments, showPost, showArticle]);

  // 判断是否展示输入框
  const allTypeState = useMemo(() => {
    return [showVideo, showPodcast, showMoments, showPost, showArticle].some((item) => {
      if (item) {
        return true;
      }
    });
  }, [showVideo, showPodcast, showMoments, showPost, showArticle]);

  // 判断是否展示链接按钮（link_url）
  const showLinkButton = useMemo(() => {
    return applyInputTypeInfo?.[INPUT_TYPE_MAP[inputType]]?.split(',')?.includes('2');
  }, [applyInputTypeInfo, inputType])

  // 判断是否展示文档按钮（doc_id）
  const showDocumentButton = useMemo(() => {
    return applyInputTypeInfo?.[INPUT_TYPE_MAP[inputType]]?.split(',')?.includes('3');
  }, [applyInputTypeInfo, inputType])

  const switchMode = async (m: InputMode) => {
    if (m !== InputMode.voice) {
      if (mode !== m) setMode(m);
      return;
    }
    await toggle();
  };

  useEffect(() => {
    const el = scriptRef.current;
    if (!el) return;
    const onFocusHandler = () => onShowTemplatesChange(true);
    el.addEventListener('focus', onFocusHandler);
    return () => el.removeEventListener('focus', onFocusHandler);
  }, []);

  // 视频模板列表特殊处理
  useEffect(() => {
    if (!videoTemplates.length) return;
    const notExist = videoTemplates.every((item) => item.id !== selectedVideoTemplate?.id);
    if (notExist) {
      setSelectedVideoTemplate(videoTemplates[0]);
      return;
    }
    // 有最新引用，同步最新引用
    const latest = videoTemplates.find((item) => item.id === selectedVideoTemplate?.id);
    if (latest && latest !== selectedVideoTemplate) {
      setSelectedVideoTemplate(latest);
    }
  }, [videoTemplates]);

  useEffect(() => {
    // if (videoTemplates.every((item) => item.id !== selectedVideoTemplate?.id)) {
    //   setSelectedVideoTemplate(videoTemplates[0]);
    // }
    if (voiceTemplates.every((item) => item.id !== selectedVoiceTemplate?.id)) {
      setSelectedVoiceTemplate(voiceTemplates[0]);
    }
    if (momentTemplates.every((item) => item.id !== selectedMomentTemplate?.id)) {
      setSelectedMomentTemplate(momentTemplates[0]);
    }
    if (postTemplates.every((item) => item.id !== selectedPostTemplate?.id)) {
      setSelectedPostTemplate(postTemplates[0]);
    }
    if (articleTemplates.every((item) => item.id !== selectedArticleTemplate?.id)) {
      setSelectedArticleTemplate(articleTemplates[0]);
    }
  }, [videoTemplates, voiceTemplates, momentTemplates, postTemplates, articleTemplates]);

  // 是否为智能体创建模式（视频、播客、朋友圈、图文、文章）
  const isAgentWorkflow = [1, 2, 3, 4, 5].includes(inputType);
  // 是否为短视频
  const isVideoType = [1].includes(inputType);
  // 是否为播客
  const isPodcastType = [2].includes(inputType);
  // 是否为Ai创作
  const isAiCreation = [6].includes(inputType);
  // 是否支持素材范围选择（短视频）
  const isOpenMaterialRange = [1].includes(inputType);
  // 是否支持口播模式（短视频、播客）
  const isOpenLiveStream = [1, 2].includes(inputType);
  // 当前处于短视频、播客的口播模式下
  const isScriptOnly = isOpenLiveStream && liveStreamScript;
  // 统一最大输入文本长度：
  // - 口播模式下：短视频 2400，播客 4800
  // - 其余模式：默认 2400
  const maxInputCount = isScriptOnly
    ? (isVideoType ? VIDEO_LIVE_SCRIPT_MAX_COUNT : PODCAST_LIVE_SCRIPT_MAX_COUNT)
    : WORKFLOW_SCRIPT_DEFAULT_MAX_COUNT;

  // 当模式切换导致最大字符数变小（如播客口播 4800 -> 普通模式 2400）时，自动截断到当前上限。
  useEffect(() => {
    const trimmed = trimToWordCount(inputText, maxInputCount);
    if (trimmed === inputText) return;

    if (scriptMarks.length && onScriptMarksChange) {
      onScriptMarksChange(reconcileScriptReplaceMarks(inputText, trimmed, scriptMarks));
    }
    setValue(trimmed);
  }, [inputText, maxInputCount, scriptMarks, onScriptMarksChange, setValue]);

  // 判断是否可以提交的条件：
  // 有文档/链接(type不能为6)
  // 有参考图(type为6)
  // 有文本
  const canSubmit =
    ((noteId || linkUrl) && isAgentWorkflow)
    || (referenceImages?.length > 0 && isAiCreation)
    || inputText.trim();

  // ===== 积分计算（与展示一致，提交时带给接口）=====
  // 针对口播短视频/播客文案，120字符阶梯
  const contentSteps = Math.max(1, Math.ceil(Math.max(inputTextCount, 1) / LIVE_SCRIPT_COUNT));
  // 针对视频时长，15秒阶梯
  const durationSeconds = getEstimatedDurationSeconds(estimatedDuration);
  const durationSteps = Math.max(1, Math.ceil(Math.max(durationSeconds, 1) / VIDEO_DURATION_STEP));
  // 工作流积分
  const workflowCredits = isAgentWorkflow
    ? getWorkflowCredits(inputType, isScriptOnly, outLang, { contentSteps, durationSteps })
    : 0;
  const aiCreationCredits = isAiCreation ? getAiCreationCredits(aiCreationModel, aiCreationParams) : 0;
  // 音频预览积分
  const audioPreviewCredits = getAudioPreviewCredits(inputTextCount);
  const displayCredits = isAgentWorkflow ? workflowCredits : aiCreationCredits;
  const showCredits = displayCredits > 0;
  // ===== 积分计算 End ===== 
  console.log('displayCredits:', { inputTextCount, contentSteps, displayCredits, durationSteps })

  const submit = async (opts?: { skipScriptIllegalCheck?: boolean }) => {
    if (!canSubmit || submitLoading) return;
    // 校验过期
    if (isExpired) {
      toast.default({ description: t('agent_creation.unsubscribed.expired', { expiredAt: formatDateToYYYYMMDD(expiredAt) }) });
      return;
    }
    // 校验异常
    if (isException) {
      toast.default({ description: t('agent_creation.warning_tips') });
      return;
    }
    // 口播模式下，校验输入文本是否超过最大长度
    if (inputTextCount > maxInputCount) {
      toast.default({ description: t('live_script.max_length_toast', { count: maxInputCount }) });
      return;
    }

    const trimmed = inputText.trim();

    // 锁定操作（By音频生成中）
    if (shouldBlockByAudioGenerating) {
      showAudioGeneratingToast();
      return;
    }

    // 不跳过违禁词检测、口播模式下、有输入文本、有违禁词检测函数，有inputText、 则进行违禁词检测
    if (
      !opts?.skipScriptIllegalCheck &&
      openScriptIllegal &&
      isScriptOnly &&
      trimmed &&
      onCheckScriptIllegal
    ) {
      const illegalGate = await runScriptIllegalSubmitGate({
        trimmed,
        onCheckScriptIllegal,
        scriptRiskWords,
        onIsScriptIllegalCheckFreshForText,
        failMessage: t('agent_creation.input.script_illegal_check_failed'),
        onCheckFailed: (msg) => toast.default({ description: msg }),
      });
      if (illegalGate === 'show_modal') {
        setShowRiskTipsModal(true);
        return;
      }
      if (illegalGate === 'error') return;
    }

    setSubmitLoading(true);
    try {
      if (inputType === 1) {
        const templateId = selectedVideoTemplate?.id;
        if (!templateId) { toast.default({ description: t('agent_creation.generate.no_template') }); return; }
        await onSubmit?.({
          content: trimmed,
          inputType,
          templateId,
          linkUrl: showLinkButton ? linkUrl : '',
          noteId: showDocumentButton ? noteId : null,
          noteName: showDocumentButton ? noteName : '',
          point: displayCredits,
          ...(isScriptOnly && scriptMarks.length ? { scriptMarks } : {}),
        });
      } else if (inputType === 2) {
        const voiceId = selectedVoiceTemplate?.id;
        if (!voiceId) { toast.default({ description: t('agent_creation.generate.no_template') }); return; }
        await onSubmit?.({
          content: trimmed,
          inputType,
          voiceId,
          linkUrl: showLinkButton ? linkUrl : '',
          noteId: showDocumentButton ? noteId : null,
          noteName: showDocumentButton ? noteName : '',
          point: displayCredits,
          ...(isScriptOnly && scriptMarks.length ? { scriptMarks } : {}),
        });
      } else if (inputType === 3) {
        await onSubmit?.({
          content: trimmed,
          inputType,
          linkUrl: showLinkButton ? linkUrl : '',
          noteId: showDocumentButton ? noteId : null,
          noteName: showDocumentButton ? noteName : '',
          point: displayCredits,
        });
      } else if (inputType === 4) {
        const templateId = selectedPostTemplate?.id;
        if (!templateId) { toast.default({ description: t('agent_creation.generate.no_template') }); return; }
        await onSubmit?.({
          content: trimmed,
          inputType,
          templateId,
          linkUrl: showLinkButton ? linkUrl : '',
          noteId: showDocumentButton ? noteId : null,
          noteName: showDocumentButton ? noteName : '',
          templateResource: selectedPostTemplate?.templateResource,
          point: displayCredits,
        });
      } else if (inputType === 5) {
        const templateId = selectedArticleTemplate?.id;
        if (!templateId) { toast.default({ description: t('agent_creation.generate.no_template') }); return; }
        await onSubmit?.({
          content: trimmed,
          inputType,
          templateId,
          linkUrl: showLinkButton ? linkUrl : '',
          noteId: showDocumentButton ? noteId : null,
          noteName: showDocumentButton ? noteName : '',
          templateResource: selectedArticleTemplate?.templateResource,
          point: displayCredits,
        });
      } else if (inputType === 6) {
        await onSubmit?.({
          content: trimmed,
          inputType,
        });
        setReferenceImages([]);
        onEndFrameImageChange([]);
      }
    } catch (err) {
      console.error(err);
      return;
    } finally {
      setSubmitLoading(false);
    }
    setValue('');
    clearState();
  };

  const hasTemplate = useMemo(() => {
    switch (inputType) {
      case 1: return (videoTemplates?.length ?? 0) > 0;
      // 播客一直展示
      case 2: return 1;
      case 3: return (momentTemplates?.length ?? 0) > 0;
      case 4: return (postTemplates?.length ?? 0) > 0;
      case 5: return (articleTemplates?.length ?? 0) > 0;
      default: return false;
    }
  }, [inputType, videoTemplates, voiceTemplates, momentTemplates, postTemplates, articleTemplates]);

  // 清空文档和链接
  const clearState = () => {
    setLinkUrl('');
    setNoteId(null);
    setNoteName('');

    onScriptMarksChange?.([]); // 清空口播台词读音标记
    onClearScriptRiskWords?.(); // 清空脚本违禁词检测
    onClearTTSCreateResult?.(); // 清空试听语音
  }

  // 提取文本中的第一个链接
  const extractFirstLink = (text: string) => {
    const match = text.match(URL_REGEX);
    // const link = findFirstValidLink(text)
    // console.log('match----------', {match})
    if (!match) return { text };
    const link = match[0];
    // 只移除第一次出现的该链接
    const newText = text.replace(link, '').trim();
    return {
      link: link.startsWith('www.') ? `https://${link}` : link,
      text: newText,
    };
  }

  // 处理粘贴链接
  const handlePasteLink = async () => {
    const text = await navigator.clipboard.readText();
    const result = extractFirstLink(text);
    if (!result) return;
    setLinkUrl(result.link);
    setValue(inputText + result.text);
    // 清空文档
    setNoteId(null);
    setNoteName('');
  };

  // 处理输入变化
  const handleInputChange = (text: string) => {
    // 截断输入文本，按当前口播类型限制最大字符数
    text = trimToWordCount(text, maxInputCount);
    const applyText = (next: string) => {
      if (scriptMarks.length && onScriptMarksChange && !isScriptOnly) {
        onScriptMarksChange(reconcileScriptReplaceMarks(inputText, next, scriptMarks));
      }
      setValue(next);
    };
    if (!showLinkButton) {
      applyText(text);
      return;
    }
    const result = extractFirstLink(text);
    if (result && !showLabel && !noteId) {
      setLinkUrl(result.link);
      applyText(result.text);
      // 清空文档
      setNoteId(null);
      setNoteName('');
    } else {
      applyText(text);
    }
  };

  // 处理文档选择确认
  const handleDocumentConfirm = async (knowledgeItems: {
    id: number; parentId: number; name: string
  }[]) => {
    setNoteId(knowledgeItems[0].parentId)
    setNoteName(knowledgeItems[0].name || t('assets.knowledge_item.untitled_note'))
    // 清空链接
    setLinkUrl('');
  };

  const showAudioGeneratingToast = () => {
    toast.default({ description: t('live_script.audio_generating') });
  };
  const showScriptIllegalCheckingToast = () => {
    toast.default({ description: t('illegal_word.script_illegal_checking') });
  };

  // 处理口播台词开关
  const handleLiveStreamScriptChange = (next: boolean) => {
    if (shouldBlockByScriptIllegalChecking && next !== liveStreamScript) {
      showScriptIllegalCheckingToast();
      return;
    }
    if (shouldBlockByAudioGenerating && !next) {
      showAudioGeneratingToast();
      return;
    }
    setLiveStreamScript(next);
  };

  // 处理工作流类型
  const handleWorkflowTypeChange = (nextType: number) => {
    if (shouldBlockByScriptIllegalChecking && nextType !== inputType) {
      showScriptIllegalCheckingToast();
      return;
    }
    if (shouldBlockByAudioGenerating && nextType !== inputType) {
      showAudioGeneratingToast();
      return;
    }
    setInputType(nextType);
  };

  // 处理模板开关
  const handleTemplateSwitchClick = (open: () => void) => {
    if (shouldBlockByScriptIllegalChecking) {
      showScriptIllegalCheckingToast();
      return;
    }
    if (shouldBlockByAudioGenerating) {
      showAudioGeneratingToast();
      return;
    }
    open();
  };

  // 处理模板保存
  const handleTemplateSave = (save: () => void) => {
    if (shouldBlockByScriptIllegalChecking) {
      showScriptIllegalCheckingToast();
      return;
    }
    if (shouldBlockByAudioGenerating) {
      showAudioGeneratingToast();
      return;
    }
    save();
  };

  const handleOpenScriptIllegalChange = (next: boolean) => {
    if (shouldBlockByScriptIllegalChecking && next !== openScriptIllegal) {
      showScriptIllegalCheckingToast();
      return;
    }
    setOpenScriptIllegal(next);
  };

  const showLabel = noteId ? noteName : linkUrl;

  // 模板映射
  const templateMap = useMemo(() => ({
    1: {
      templates: videoTemplates,
      selectedTemplate: selectedVideoTemplate,
      setSelectedTemplate: setSelectedVideoTemplate,
    },
    2: {
      templates: voiceTemplates,
      selectedTemplate: selectedVoiceTemplate,
      setSelectedTemplate: setSelectedVoiceTemplate,
    },
    3: {
      templates: momentTemplates,
      selectedTemplate: selectedMomentTemplate,
      setSelectedTemplate: setSelectedMomentTemplate,
    },
    4: {
      templates: postTemplates,
      selectedTemplate: selectedPostTemplate,
      setSelectedTemplate: setSelectedPostTemplate,
    },
    5: {
      templates: articleTemplates,
      selectedTemplate: selectedArticleTemplate,
      setSelectedTemplate: setSelectedArticleTemplate,
    },
  }), [videoTemplates, voiceTemplates, momentTemplates, postTemplates, articleTemplates, selectedVideoTemplate, selectedVoiceTemplate, selectedMomentTemplate, selectedPostTemplate, selectedArticleTemplate]);

  // 获取当前选中的模板
  const currentSelectedTemplate = useMemo(() => templateMap[inputType]?.selectedTemplate, [inputType, templateMap, selectedVideoTemplate, selectedVoiceTemplate, selectedMomentTemplate, selectedPostTemplate, selectedArticleTemplate]);

  // 试听语音信息参数
  const auditionVoiceInfo = useMemo(() => {
    switch (inputType) {
      case INPUT_TYPE.VIDEO: return {
        voiceId: selectedVideoTemplate?.voiceModelId,
        speedRatio: selectedVideoTemplate?.speedRatio,
      };
      case INPUT_TYPE.PODCAST: return {
        voiceId: selectedVoiceTemplate?.voiceTtsId,
      };
      default: return {};
    }
  }, [inputType, selectedVideoTemplate, selectedVoiceTemplate]);

  // 已订阅的工作流类型
  const subscriptedWorkflows = [
    {
      type: 1,
      name: t("agent_creation.video"),
      icon: <VideoTypeIcon className={cn('size-5 text-[rgba(102,112,133,1)]', inputType === 1 && 'text-[rgba(16,24,40,1)]')} />,
      show: showVideo,
    },
    {
      type: 6,
      name: t("agent_creation.ai_creation"),
      icon: <AiCreationTypeIcon className={cn('size-5 text-[rgba(102,112,133,1)]', inputType === 6 && 'text-[rgba(16,24,40,1)]')} />,
      show: showAiCreation,
    },
    {
      type: 4,
      name: t("agent_creation.post"),
      icon: <PostTypeIcon className={cn('size-5 text-[rgba(102,112,133,1)]', inputType === 4 && 'text-[rgba(16,24,40,1)]')} />,
      show: showPost,
    },
    {
      type: 5,
      name: t("agent_creation.article"),
      icon: <ArticleTypeIcon className={cn('size-5 text-[rgba(102,112,133,1)]', inputType === 5 && 'text-[rgba(16,24,40,1)]')} />,
      show: showArticle,
    },
    {
      type: 2,
      name: t("agent_creation.podcast"),
      icon: <PodcastTypeIcon className={cn('size-5 text-[rgba(102,112,133,1)]', inputType === 2 && 'text-[rgba(16,24,40,1)]')} />,
      show: showPodcast,
    },
    {
      type: 3,
      name: t("agent_creation.moments"),
      icon: <MomentsTypeIcon className={cn('size-5 text-[rgba(102,112,133,1)]', inputType === 3 && 'text-[rgba(16,24,40,1)]')} />,
      show: showMoments,
    },
  ]

  // 模型是否支持首尾帧图片
  const isFontEndFrameImage = getModelOptionByValue(aiCreationModel)?.isFontEndFrameImage && isAiCreation

  return allTypeState && (
    <div
      className={cn(
        'relative mx-auto mb-4 border border-[rgba(234,236,240,1)] max-w-[680px] bg-white rounded-[20px] shadow-lg transition-all duration-300 ease-in-out overflow-hidden',
        'min-h-[94px]'
      )}
    >
      <div
        className={cn(
          "grid transition-all duration-300 ease-in-out",
          showTemplates ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0 pointer-events-none"
        )}
      >
        <div className="overflow-hidden">
          <div className='flex items-center px-[13px] py-4 gap-1.5 select-none'>
            {subscriptedWorkflows?.filter((item) => item.show).map((item) => (
              <div
                key={item.type}
                className={cn(
                  'group py-1.5 px-[10px] flex items-center cursor-pointer hover:bg-[rgba(16,24,40,0.05)] max-w-max rounded-[8px] gap-2 transition-all',
                  item.type === inputType && 'bg-[rgba(16,24,40,0.05)]',
                )}
                onClick={() => handleWorkflowTypeChange(item.type)}
              >
                {item.icon}
                <span className={cn(
                  'text-[rgba(102,112,133,1)] text-sm',
                  item.type === inputType && 'text-[rgba(16,24,40,1)]'
                )}>{item.name}</span>
              </div>
            ))}
          </div>
          <div className='w-full flex items-start px-[13px]'>
            {isAiCreation && (
              <AiCreationButton
                aiCreationType={aiCreationType}
                aiCreationModel={aiCreationModel}
                referenceImages={referenceImages}
                frameType={isFontEndFrameImage ? 'font' : null}
                onReferenceImagesChange={setReferenceImages}
                disabledRemove={isFontEndFrameImage && referenceImages.length > 0 && endFrameImage.length > 0}
                uploadReferenceImages={onUploadReferenceImages}
                uploadReferenceImagesLoading={uploadReferenceImagesLoading}
              />
            )}
            {isFontEndFrameImage && (
              <>
                <div
                  className='self-center'
                  onClick={(e) => {
                    e.stopPropagation()
                    onSwitchFirstAndEndFrame()
                  }}
                >
                  <SwitchHorizontalIcon className='mx-1.5 mb-1.5 cursor-pointer flex-shrink-0 text-[rgba(152,162,179,1)] hover:text-[rgba(71,84,103,1)] active:scale-95 transition-all' />
                </div>
                <AiCreationButton
                  aiCreationType={aiCreationType}
                  aiCreationModel={aiCreationModel}
                  referenceImages={endFrameImage}
                  uploadReferenceImagesLoading={endFrameImageLoading}
                  frameType={isFontEndFrameImage ? 'end' : null}
                  disabled={isFontEndFrameImage && referenceImages.length === 0}
                  onReferenceImagesChange={onEndFrameImageChange}
                  uploadReferenceImages={onUploadEndFrameImage}
                />
              </>
            )}
            {isAgentWorkflow && hasTemplate && (
              <div className='min-w-[52px] max-w-[52px] h-[70px] rounded-[8px] bg-gray-100'>
                <Tooltip
                  side='top'
                  align='center'
                  trigger={
                    !isPodcastType ? (
                      <div
                        className='relative size-full select-none hover:scale-105 transition-all hover:shadow-sm'
                        onClick={() => {
                          handleTemplateSwitchClick(() => {
                            if (inputType === 1) {
                              setShowVideoTemplateChooseModal(true);
                            } else {
                              setShowTemplateChooseModal(true);
                            }
                          });
                        }}
                      >
                        <FallbackImage
                          primarySrc={buildImageOssResize(currentSelectedTemplate?.coverUrl || '', { w: 306, h: 408 })} // 尺寸跟模板弹窗里面一致,就会拿缓存
                          fallbackSrc={currentSelectedTemplate?.coverUrl || ''}
                          alt={currentSelectedTemplate?.name || ''}
                          className='min-w-[52px] max-w-[52px] h-[70px] object-cover rounded-[8px] cursor-pointer flex-shrink-0 border border-[#eef0f4] '
                        />
                        <ChangeIcon className="absolute bottom-1 right-1 w-5 h-5 text-[rgba(16,24,40,1)] pointer-events-none" />
                      </div>
                    ) : (
                      // 播客模板
                      <div className="inline-flex h-20">
                        <VoiceTemplateCapsuleItemNew
                          key='voice'
                          label={templateMap[inputType]?.selectedTemplate?.name}
                          audioUrl={templateMap[inputType]?.selectedTemplate?.sampleUrl}
                          showCreate={!voiceTemplates.length}
                          // onTap={() => setSelectedVoiceTemplate(t)}
                          onClick={() => handleTemplateSwitchClick(() => setShowVoiceTemplateModal(true))}
                        />
                      </div>
                    )
                  }
                  content={
                    <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                      <span className='text-[rgba(255,255,255,1)] text-sm'>
                        {inputType !== 2
                          ? t('agent_creation.input.change_template') :
                          voiceTemplates.length ? t('agent_creation.input.change_voice') : t('input_add_voice')
                        }
                      </span>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                />
              </div>
            )}
            {/* 短视频没模板，展示添加按钮 */}
            {isAgentWorkflow && !hasTemplate && isVideoType && (
              <div onClick={() => handleTemplateSwitchClick(() => setShowVideoTemplateChooseModal(true))}>
                <VideoTemplateAddButton showTooltip={true} onSuccess={onRefreshVideoTemplates} />
              </div>
            )}
            <div
              className='relative w-full ml-[9px]'
              style={isScriptOnly
                ? ({ '--script-text-start': `${scriptLabelWidth + 10}px` } as React.CSSProperties)
                : undefined}
            >
              {/* 口播标签 */}
              {isOpenLiveStream && (
                <LiveScriptLabel
                  liveStreamScript={liveStreamScript}
                  setLiveStreamScript={handleLiveStreamScriptChange}
                  onWidthChange={setScriptLabelWidth}
                />
              )}
              <ScriptCreation
                ref={scriptRef}
                value={inputText}
                maxCount={maxInputCount}
                tooltip={false}
                marks={scriptMarks}
                enableWordSelection={isScriptOnly}
                openScriptIllegal={openScriptIllegal}
                riskWords={scriptRiskWords}
                onMarksChange={onScriptMarksChange}
                onChange={handleInputChange}
                maxLineHeight={6}
                className={cn(
                  "h-[70px] pl-[3px] leading-[24px] tracking-[0.8px]",
                  isScriptOnly && "indent-[calc(var(--script-text-start)-3px)]"
                )}
                readOnly={shouldBlockByAudioGenerating || shouldBlockByScriptIllegalChecking}
                scriptPlaceholder={t('agent_creation.input.script_placeholder')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
              />
            </div>
          </div>
          <div className="w-full">
            <div className={cn('flex items-end m-4 mt-[10px] justify-between',
            )}>
              {/* Ai创作相关的操作按钮 */}
              {isAiCreation && (
                <div className='flex items-center gap-2'>
                  <CreationTypeButton
                    type={aiCreationType}
                    onModeChange={setAiCreationType}
                  />
                  <ModelButton
                    model={aiCreationModel}
                    aiCreationType={aiCreationType}
                    onModelChange={setAiCreationModel}
                    referenceImages={referenceImages}
                  />
                  <ParameterButton
                    hasReferenceImage={aiCreationType === 'video' && referenceImages?.length > 0}
                    params={aiCreationParams}
                    aiCreationType={aiCreationType}
                    aiCreationModel={aiCreationModel}
                    onParamsChange={setAiCreationParams}
                  />
                </div>
              )}

              <div className='flex items-center'>
                {/* 文档/粘贴链接操作和展示 */}
                {(showLinkButton || showDocumentButton) && !isScriptOnly && (
                  <div className={cn(
                    "flex items-center gap-3 min-h-9",
                    isScriptOnly && "opacity-50"
                  )}>
                    <MoreActionPopover
                      className="w-40"
                      side="bottom"
                      disabled={isScriptOnly}
                      tiggerIcon={
                        <Tooltip
                          side='top'
                          align='center'
                          trigger={
                            <div
                              className={cn(
                                "size-9 flex items-center justify-center rounded-[8px] hover:bg-[rgba(242,244,247,1)] transition-colors flex-shrink-0",
                                isScriptOnly && "!cursor-not-allowed"
                              )}
                            >
                              <PaperclipIcon />
                            </div>
                          }
                          content={
                            <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                              <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                                {!isScriptOnly ? t('agent_creation.input.attachment') : t("live_script.before_use_close_tooltip")}
                              </span>
                              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                            </div>
                          }
                        />
                      }
                    >
                      {showDocumentButton && (
                        <MoreActionItem
                          icon={<Plus className="w-5 h-5 flex-shrink-0" />}
                          className='text-[rgba(16,24,40,1)] font-medium'
                          label={!noteId ? t('agent_creation.input.add_document') : t('agent_creation.input.replace_document')}
                          onClick={() => setShowDocumentModal(true)}
                        />
                      )}
                      {showLinkButton && (
                        <MoreActionItem
                          icon={<LinkIcon className="w-5 h-5 flex-shrink-0" />}
                          className='text-[rgba(16,24,40,1)] font-medium'
                          label={t('agent_creation.input.paste_link')}
                          onClick={handlePasteLink}
                        />
                      )}
                    </MoreActionPopover>
                    <div className="min-w-0">
                      {showLabel && (
                        <div className='flex items-center max-w-[170px] rounded-[8px] bg-[rgba(242,244,247,1)] py-2 px-3 gap-1.5 mr-2'>
                          <TruncatedText tooltipSide="top" className='text-sm text-[rgba(16,24,40,1)] font-semibold' text={showLabel} />
                          <X className="size-5 cursor-pointer flex-shrink-0" onClick={clearState} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className='flex gap-1 items-center'>
                  {/* 语言选择（播客、朋友圈、图文、文章） */}
                  {!isVideoType && isAgentWorkflow && !isScriptOnly && (
                    <OutputLangSelect
                      outLang={outLang}
                      setOutLang={setOutLang}
                    />
                  )}
                  {/* 语言/时长选择（短视频） */}
                  {isVideoType && isAgentWorkflow && !isScriptOnly && (
                    <OutputSettingButton
                      outLang={outLang}
                      setOutLang={setOutLang}
                      estimatedDuration={estimatedDuration}
                      setEstimatedDuration={setEstimatedDuration}
                    />
                  )}
                  {/* 口播按钮 */}
                  {isOpenLiveStream && (
                    <LiveScriptButton
                      liveStreamScript={liveStreamScript}
                      setLiveStreamScript={handleLiveStreamScriptChange}
                    />
                  )}
                  {/* 素材范围设置 */}
                  {isOpenMaterialRange && (
                    <MaterialRangeSettingButton
                      scene={selectedVideoTemplate?.scene}
                      enabledNote={!!noteId}
                    />
                  )}
                  {/* 脚本违禁词检测按钮 */}
                  {isScriptOnly && (
                    <ScriptIllegalButton
                      openScriptIllegal={openScriptIllegal}
                      setOpenScriptIllegal={handleOpenScriptIllegalChange}
                      showRiskTipsModal={showRiskTipsModal}
                      setShowRiskTipsModal={setShowRiskTipsModal}
                      onContinue={() => submit({ skipScriptIllegalCheck: true })}
                      onCancel={() => setShowRiskTipsModal(false)}
                    />
                  )}
                  {/* 口播模式下：试听台词按钮 */}
                  {isScriptOnly && (
                    <AuditionButton
                      text={inputText}
                      voiceId={auditionVoiceInfo?.voiceId}
                      speedRatio={auditionVoiceInfo?.speedRatio}
                      marks={scriptMarks}
                      previewCredits={audioPreviewCredits}
                      isAudioGenerating={isAudioGenerating}
                      readOnly={scriptIllegalChecking}
                      onGenerateAudio={onGenerateAudio}
                      managedAudioUrl={managedAudioUrl}
                      onBeforeGenerate={() => {
                        if (!currentSelectedTemplate || !currentSelectedTemplate?.id) {
                          toast.default({ description: t('live_script.audio_preview_no_template') });
                          return false;
                        }
                        return true;
                      }}
                    />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* {stopIn !== null && (
                  <span className="text-sm text-[rgba(16,24,40,1)]">{t('agent_creation.recording.recording_tips', { seconds: stopIn })}</span>
                )}
                <Tooltip
                  side="top"
                  align="center"
                  mode="hybrid"
                  trigger={
                    <IconButton
                      aria-label={t('agent_creation.recording.recording_input')}
                      onClick={() => switchMode(InputMode.voice)}
                      className={cn("w-8 h-8", (stopIn !== null || recording) ? "bg-[#ef4444] hover:bg-[#dc2626] text-white" : "")}
                      icon={(stopIn !== null || recording) ? <StopIcon className="w-6 h-6" /> : <RecordIcon className="w-6 h-6" />}
                    />
                  }
                  content={
                    <div className="relative">
                      <div className="px-3 py-2 rounded-[8px] bg-[rgba(16,24,40,1)] text-sm font-medium text-white">
                        {stopIn !== null || recording ? t('agent_creation.recording.recording_stop') : t('agent_creation.recording.recording_input')}
                      </div>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                    </div>
                  }
                /> */}
                {isScriptOnly && (
                  <InputTextCount
                    maxCount={maxInputCount}
                    displayMaxCount={isVideoType ? 450 : 500}
                    currentCount={inputTextCount}
                  />
                )}
                {isScriptOnly && inputTextCount > (isVideoType ? 450 : 500) && (
                  <span className="w-px h-5 bg-[rgba(229,231,235,1)]" />
                )}
                <div className='flex items-center'>
                  {showCredits && !scriptIllegalChecking && (
                    <AgentCredits
                      credits={displayCredits}
                      tooltipEnabled={liveStreamScript}
                    />
                  )}
                  {isScriptOnly && scriptIllegalChecking && <WordsRiskChecking />}
                  {/* 发送/禁用/取消按钮 */}
                  {!(isScriptOnly && scriptIllegalChecking) && (
                    <IconButton
                      aria-label={t('agent_creation.input.send')}
                      disabled={submitLoading}
                      onClick={() => submit()}
                      className={cn(
                        "w-9 h-9 transition-colors",
                        // 默认颜色（灰色/禁用感）
                        "text-[rgba(208,213,221,1)] hover:bg-transparent cursor-not-allowed",
                        // 激活颜色
                        canSubmit && !submitLoading && "text-[rgba(16,24,40,1)] cursor-pointer",
                      )}
                      icon={<SendIcon className="w-9 h-9" />}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 收起内容 */}
      <div
        className={cn(
          "flex items-center gap-5 p-4 transition-all duration-400 ease-in-out absolute top-0 left-0 w-full min-h-[94px]",
          !showTemplates
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-2 pointer-events-none"
        )}
      >
        {inputType === 2 ? (
          <div className='p-1 w-[45px] h-[60px] flex items-center justify-center bg-[rgba(234,236,240,1)] rounded-[6px]'>
            <TruncatedText as="p" text={selectedVoiceTemplate?.name ?? ''} className="text-[13px] font-medium text-black truncate max-w-[120px]" />
          </div>
        ) : (
          <Image
            className="w-[46px] h-[60px] object-cover rounded-[6px] shrink-0 cursor-pointer"
            src={
              inputType === 1 ? selectedVideoTemplate?.coverUrl :
                inputType === 3 ? selectedMomentTemplate?.coverUrl :
                  inputType === 4 ? selectedPostTemplate?.coverUrl :
                    inputType === 5 ? selectedArticleTemplate?.coverUrl : undefined
            }
            alt="current template"
            onClick={() => onShowTemplatesChange(true)}
          />
        )}
        <input
          type="text"
          value={inputText}
          readOnly
          onClick={() => onShowTemplatesChange(true)}
          onFocus={() => onShowTemplatesChange(true)}
          onChange={(e) => handleInputChange(e.target.value)}
          className="flex-1 text-sm border-none rounded-md placeholder:text-[#98A2B3] focus:outline-none focus:none h-14"
          placeholder={t('agent_creation.input.script_placeholder')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        {/* {stopIn !== null && (
          <span className="text-sm text-[rgba(16,24,40,1)]">{t('agent_creation.recording.recording_tips', { seconds: stopIn })}</span>
        )} */}
        {/* <Tooltip
          side="top"
          align="center"
          mode="hybrid"
          trigger={
            <IconButton
              aria-label={t('agent_creation.recording.recording_input')}
              onClick={() => switchMode(InputMode.voice)}
              className={cn("w-8 h-8", (stopIn !== null || recording) ? "bg-[#ef4444] hover:bg-[#dc2626] text-white" : "")}
              icon={(stopIn !== null || recording) ? <StopIcon className="w-6 h-6" /> : <RecordIcon className="w-6 h-6" />}
            />
          }
          content={
            <div className="relative">
              <div className="px-3 py-2 rounded-[8px] bg-[rgba(16,24,40,1)] text-sm font-medium text-white">
                {stopIn !== null || recording ? t('agent_creation.recording.recording_stop') : t('agent_creation.recording.recording_input')}
              </div>
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
            </div>
          }
        /> */}
        {/* <span className="w-px h-5 bg-[rgba(229,231,235,1)]" /> */}
        <div className='flex items-center'>
          {showCredits && !scriptIllegalChecking && (
            <AgentCredits
              credits={displayCredits}
              tooltipEnabled={liveStreamScript}
            />
          )}
          {isScriptOnly && scriptIllegalChecking && <WordsRiskChecking />}
          {!(isScriptOnly && scriptIllegalChecking) && (
            <IconButton
              aria-label={t('agent_creation.input.send')}
              disabled={submitLoading}
              onClick={() => submit()}
              className={cn(
                "w-9 h-9 transition-colors",
                // 默认颜色（灰色/禁用感）
                "text-[rgba(208,213,221,1)] hover:bg-transparent cursor-not-allowed",
                // 激活颜色
                canSubmit && !submitLoading && "text-[rgba(16,24,40,1)] cursor-pointer",
              )}
              icon={<SendIcon className="w-9 h-9" />}
            />
          )}
        </div>
      </div>
      {/* 选择文档 */}
      {showDocumentModal && (
        <DocumentChooseModal
          open={showDocumentModal}
          onOpenChange={setShowDocumentModal}
          onConfirm={handleDocumentConfirm}
        />
      )}
      {/* 选择模板 */}
      {showTemplateChooseModal && (
        <TemplateChooseModal
          open={showTemplateChooseModal}
          onOpenChange={setShowTemplateChooseModal}
          onSave={(template) => handleTemplateSave(() => templateMap[inputType]?.setSelectedTemplate(template))}
          templates={templateMap[inputType]?.templates}
          selectedId={templateMap[inputType]?.selectedTemplate?.id}
        />
      )}
      {/* 选择视频模板 */}
      {showVideoTemplateChooseModal && (
        <VideoTemplateChooseModal
          open={showVideoTemplateChooseModal}
          templates={videoTemplates}
          selectedId={selectedVideoTemplate?.id}
          onSave={(template) => handleTemplateSave(() => setSelectedVideoTemplate(template))}
          onOpenChange={setShowVideoTemplateChooseModal}
          onCreateTemplateSuccess={onRefreshVideoTemplates}
        />
      )}
      {/* 选择语音模板 */}
      {showVoiceTemplateModal && (
        <TemplateVoiceModal
          open={showVoiceTemplateModal}
          onOpenChange={setShowVoiceTemplateModal}
          onSave={(template) => handleTemplateSave(() => setSelectedVoiceTemplate(template))}
          voices={voiceTemplates}
          voiceTemplateLoading={voiceTemplateLoading}
          selectedId={selectedVoiceTemplate?.id}
          saveVoiceTemplateLoading={saveVoiceTemplateLoading}
          onRemoveVoiceTemplate={onRemoveVoiceTemplate}
          onSaveVoiceTemplate={onSaveVoiceTemplate}
        />
      )}
    </div>
  );
};

/** 输入文本计数 */
const InputTextCount = ({
  maxCount = 500,
  displayMaxCount = maxCount,
  currentCount,
  className
}: {
  maxCount: number,
  displayMaxCount?: number,
  currentCount: number,
  className?: string
}) => {
  return (
    currentCount >= displayMaxCount ? (
      <span
        className={cn(
          'text-xs text-[rgba(152,162,179,1)]',
          currentCount > maxCount && 'text-[rgba(247,144,9,1)]',
          className
        )}
      >{currentCount}/{maxCount}</span>
    ) : null
  )
}

/** 敏感词检测中 */
const WordsRiskChecking = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1 px-2 p-1.5 rounded-full bg-[rgba(16,24,40,0.05)] select-none">
      <Lottie
        className="size-[18px] flex-shrink-0"
        animationData={searchJson}
        loop
      />
      <span className="text-xs text-[rgba(16,24,40,1)]">{t('agent_creation.input.script_illegal_checking')}</span>
    </div>
  );
};

/** 积分显示 */
const AgentCredits = ({
  credits,
  tooltipEnabled,
}: {
  credits: number;
  tooltipEnabled?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <HintTooltip
      // text={t('live_script.point_tips')}
      text={t('live_script.real_time_point_tips', { point: credits })}
      enabled={tooltipEnabled}
      trigger={
        <div className='flex items-center pr-3 select-none max-w-max'>
          <AgentCreditsIcon className="size-5 pr-1.5 flex-shrink-0" />
          <span className='text-sm font-semibold text-[rgba(16,24,40,1)]'>
            {credits}
          </span>
        </div>
      }
    />
  )
}